/* =================================================================
   GHOSTFIT V3 — STOREFRONT INTERACTIONS (Shopify-wired)
   =================================================================
   Vanilla JS, no dependencies. Loads only on layout/gf-v3.liquid
   pages (the v4 templateSuffix templates). Wired to Shopify's real
   /cart.js + /cart/add.js endpoints. Skips animations in theme
   editor (Shopify.designMode) to avoid editor lifecycle quirks.

   XSS-safe: all dynamic strings via textContent / createElement;
   only static SVG markup uses insertAdjacentHTML.
   ================================================================= */
(() => {
  'use strict';

  /* DesignMode = theme editor preview. Skip motion + cart polls there. */
  const DESIGN_MODE = !!(window.Shopify && window.Shopify.designMode);

  /* DOM helpers */
  const el = (tag, attrs = {}, ...children) => {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (v == null) continue;
      if (k === 'class') node.className = v;
      else if (k === 'style') node.setAttribute('style', v);
      else if (k === 'dataset') Object.assign(node.dataset, v);
      else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2), v);
      else node.setAttribute(k, v);
    }
    for (const c of children) {
      if (c == null || c === false) continue;
      node.appendChild(c instanceof Node ? c : document.createTextNode(String(c)));
    }
    return node;
  };
  const svgFromString = (s) => {
    const wrap = document.createElement('div');
    wrap.insertAdjacentHTML('afterbegin', s);
    return wrap.firstElementChild;
  };
  const clear = (n) => { while (n.firstChild) n.removeChild(n.firstChild); };
  const $ = (s, root = document) => root.querySelector(s);
  const $$ = (s, root = document) => root.querySelectorAll(s);

  function trapFocus(container) {
    const FOCUSABLE = 'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])';
    function onKey(e) {
      if (e.key !== 'Tab') return;
      const items = Array.from(container.querySelectorAll(FOCUSABLE)).filter(n => n.offsetParent !== null);
      if (!items.length) return;
      const first = items[0]; const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  }

  const liveRegion = el('div', { role: 'status', 'aria-live': 'polite', class: 'sr-only', id: 'gf-live-region' });
  document.body.appendChild(liveRegion);
  function announce(msg) { liveRegion.textContent = ''; setTimeout(() => liveRegion.textContent = msg, 50); }

  const HOODIE_SVG = '<svg viewBox="0 0 200 240"><path d="M40 50 Q40 30 60 30 L80 30 Q90 50 100 50 Q110 50 120 30 L140 30 Q160 30 160 50 L168 90 L150 100 L150 220 Q150 230 140 230 L60 230 Q50 230 50 220 L50 100 L32 90 Z"/></svg>';
  const BAG_SVG = '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 6h15l-1.5 9h-12z"/><path d="M6 6 5 3H2"/></svg>';

  /* =================================================================
     1. THEME TOGGLE (light/dark override)
     ================================================================= */
  (() => {
    let stored;
    try { stored = localStorage.getItem('gf_theme'); } catch (_) {}
    if (stored === 'dark' || stored === 'light') {
      document.documentElement.setAttribute('data-theme', stored);
    }
    document.querySelectorAll('[data-theme-toggle]').forEach(btn => {
      btn.addEventListener('click', () => {
        const current = document.documentElement.getAttribute('data-theme')
          || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
        const next = current === 'dark' ? 'light' : 'dark';
        document.documentElement.setAttribute('data-theme', next);
        try { localStorage.setItem('gf_theme', next); } catch (_) {}
      });
    });
  })();

  /* =================================================================
     2. MOBILE MENU DRAWER
     ================================================================= */
  (() => {
    const menu = $('#mobile-menu');
    if (!menu) return;
    const backdrop = $('#mobile-menu-backdrop');
    const open = $('#nav-hamburger');
    const close = $('#mobile-menu-close');
    let untrap = null;
    function openMenu() {
      menu.classList.add('is-open');
      backdrop?.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      untrap = trapFocus(menu);
      menu.querySelector('a, button')?.focus();
    }
    function closeMenu() {
      menu.classList.remove('is-open');
      backdrop?.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (untrap) untrap();
      open?.focus();
    }
    open?.addEventListener('click', openMenu);
    close?.addEventListener('click', closeMenu);
    backdrop?.addEventListener('click', closeMenu);
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && menu.classList.contains('is-open')) closeMenu(); });
  })();

  /* =================================================================
     3. STATE PILLS (homepage; if present)
     ================================================================= */
  $$('.state-pill').forEach(pill => {
    pill.addEventListener('click', () => {
      $$('.state-pill').forEach(p => p.classList.remove('is-active'));
      pill.classList.add('is-active');
    });
  });

  /* =================================================================
     4. CATEGORY FILTER PILLS (collection pages — JS hide/show)
     ================================================================= */
  (() => {
    const row = $('#filter-row'); const grid = $('#product-grid');
    if (!row || !grid) return;
    row.addEventListener('click', e => {
      const pill = e.target.closest('.filter-pill');
      if (!pill) return;
      row.querySelectorAll('.filter-pill').forEach(p => p.classList.remove('is-active'));
      pill.classList.add('is-active');
      const cat = pill.dataset.cat;
      grid.querySelectorAll('.product-card').forEach(card => {
        card.style.display = (cat === 'all' || (card.dataset.cat || '').includes(cat)) ? '' : 'none';
      });
    });
  })();

  /* =================================================================
     5. PRODUCT DETAIL — color swatches (variant switching)
     ================================================================= */
  (() => {
    const row = $('#swatch-row');
    if (!row) return;
    const colorName = $('#pd-color-name');
    const mainImg = $('#pd-main-image-img');
    const thumbs = $$('.pd-thumb');
    const variantInput = $('#pd-variant-id');

    row.addEventListener('click', e => {
      const sw = e.target.closest('.swatch');
      if (!sw) return;
      row.querySelectorAll('.swatch').forEach(s => s.classList.remove('is-active'));
      sw.classList.add('is-active');
      const c = sw.dataset.color;
      const variantId = sw.dataset.variantId;
      const imgUrl = sw.dataset.imageUrl;
      if (mainImg && imgUrl) mainImg.src = imgUrl;
      thumbs.forEach(t => t.classList.toggle('is-active', t.dataset.color === c));
      if (colorName) colorName.textContent = sw.dataset.name || c;
      if (variantInput && variantId) variantInput.value = variantId;
    });

    thumbs.forEach(thumb => thumb.addEventListener('click', () => {
      const color = thumb.dataset.color;
      const sw = row.querySelector(`.swatch[data-color="${color}"]`);
      sw?.click();
    }));
  })();

  /* =================================================================
     6. SIZE PILLS — variant switching
     ================================================================= */
  (() => {
    const row = $('#size-row');
    if (!row) return;
    const variantInput = $('#pd-variant-id');
    const ctaPrice = $('#pd-cta-price');
    const priceDisplay = $('#pd-price-display');
    row.addEventListener('click', e => {
      const pill = e.target.closest('.size-pill');
      if (!pill || pill.disabled) return;
      row.querySelectorAll('.size-pill').forEach(p => p.classList.remove('is-active'));
      pill.classList.add('is-active');
      if (variantInput && pill.dataset.variantId) variantInput.value = pill.dataset.variantId;
      if (pill.dataset.price && priceDisplay) priceDisplay.textContent = formatMoney(parseInt(pill.dataset.price, 10));
      if (pill.dataset.price && ctaPrice) ctaPrice.textContent = formatMoney(parseInt(pill.dataset.price, 10));
    });
  })();

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* =================================================================
     8. SHOPIFY CART — real /cart.js + /cart/add.js wiring
     ================================================================= */
  const Cart = (() => {
    const drawer = $('#cart-drawer');
    const backdrop = $('#cart-drawer-backdrop');
    const body = $('#cart-drawer-body');
    const footer = $('#cart-drawer-footer');
    const total = $('#cart-total');
    const badge = $('#cart-badge');
    const meterBar = $('#free-ship-meter');
    let untrap = null;

    function buildEmpty() {
      const wrap = el('div', { class: 'cart-empty' });
      const icon = el('div', { class: 'cart-empty-icon' });
      icon.appendChild(svgFromString(BAG_SVG));
      wrap.appendChild(icon);
      wrap.appendChild(el('p', { style: 'font-size:17px;color:var(--text-primary);font-weight:500;margin-bottom:6px' }, 'Your bag is empty.'));
      wrap.appendChild(el('p', { style: 'font-size:14px' }, 'Pick a school and start shopping.'));
      return wrap;
    }

    function buildLine(item) {
      const row = el('div', { class: 'cart-line' });
      const img = el('div', { class: 'cart-line-image' });
      if (item.image) {
        const i = document.createElement('img');
        i.src = item.image;
        i.alt = item.product_title || '';
        i.loading = 'lazy';
        i.style.width = '70%';
        i.style.height = '70%';
        i.style.objectFit = 'contain';
        img.appendChild(i);
      } else {
        img.appendChild(svgFromString(HOODIE_SVG));
      }
      const info = el('div', { class: 'cart-line-info' });
      info.appendChild(el('div', { class: 'cart-line-name' }, item.product_title || item.title));
      const meta = [item.variant_title, 'Qty ' + item.quantity].filter(Boolean).join(' · ');
      info.appendChild(el('div', { class: 'cart-line-meta' }, meta));
      /* Personalization line-item properties (start with _) are hidden by default,
         but in the v4 flow we want to show name + number to the customer. */
      if (item.properties) {
        const persName = item.properties._personalization_name || item.properties['Personalize Name'];
        const persNum = item.properties._personalization_number || item.properties['Personalize Number'];
        if (persName || persNum) {
          const pers = (persName || '') + (persName && persNum ? ' · #' : '') + (persNum || '');
          info.appendChild(el('div', { class: 'cart-line-meta', style: 'color:var(--accent);margin-top:2px' }, 'Personalized: ' + pers));
        }
      }
      const remove = el('button', {
        class: 't-caption',
        style: 'margin-top:6px;color:var(--accent);background:none;padding:0',
        onclick: () => removeItem(item.key),
      }, 'Remove');
      info.appendChild(remove);
      const price = el('div', { class: 'cart-line-price' }, formatMoney(item.final_line_price));
      row.appendChild(img); row.appendChild(info); row.appendChild(price);
      return row;
    }

    function buildFreeShipMeter(subtotal) {
      const THRESHOLD = 7500; // $75.00 in cents
      if (!meterBar) return;
      clear(meterBar);
      const remaining = Math.max(0, THRESHOLD - subtotal);
      const pct = Math.min(100, (subtotal / THRESHOLD) * 100);
      meterBar.classList.toggle('free-ship-meter--complete', remaining === 0);
      const text = el('div', { class: 'free-ship-meter-text' });
      if (remaining === 0) {
        text.appendChild(document.createTextNode('🎉 You\'ve unlocked '));
        text.appendChild(el('b', {}, 'free shipping'));
        text.appendChild(document.createTextNode('.'));
      } else {
        text.appendChild(document.createTextNode('Add '));
        text.appendChild(el('b', {}, formatMoney(remaining)));
        text.appendChild(document.createTextNode(' for free shipping.'));
      }
      const bar = el('div', { class: 'free-ship-meter-bar' });
      const fill = el('div', { class: 'free-ship-meter-fill', style: 'width:' + pct + '%' });
      bar.appendChild(fill);
      meterBar.appendChild(text);
      meterBar.appendChild(bar);
    }

    function render(cart) {
      if (!body) return;
      const items = cart?.items || [];
      const count = items.reduce((n, l) => n + l.quantity, 0);
      const subtotal = cart?.total_price || 0;
      if (badge) { badge.textContent = String(count); badge.classList.toggle('is-visible', count > 0); }
      clear(body);
      if (items.length === 0) {
        body.appendChild(buildEmpty());
        if (footer) footer.style.display = 'none';
        return;
      }
      items.forEach(it => body.appendChild(buildLine(it)));
      if (total) total.textContent = formatMoney(subtotal);
      if (footer) footer.style.display = 'block';
      buildFreeShipMeter(subtotal);
    }

    async function refresh() {
      try {
        const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        if (!r.ok) return;
        const cart = await r.json();
        render(cart);
        return cart;
      } catch (_) { /* offline / Shopify hiccup — keep current UI */ }
    }

    async function add(formData) {
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          announce(err.description || 'Could not add to bag');
          return null;
        }
        await refresh();
        announce('Added to bag');
        open();
        return r.json();
      } catch (_) {
        announce('Network error — try again');
        return null;
      }
    }

    async function removeItem(key) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
        await refresh();
        announce('Item removed');
      } catch (_) {}
    }

    function open() {
      drawer?.classList.add('is-open');
      drawer?.setAttribute('aria-hidden', 'false');
      backdrop?.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (drawer) untrap = trapFocus(drawer);
      drawer?.querySelector('button, a')?.focus();
    }
    function close() {
      drawer?.classList.remove('is-open');
      drawer?.setAttribute('aria-hidden', 'true');
      backdrop?.classList.remove('is-open');
      document.body.style.overflow = '';
      if (untrap) untrap();
    }

    $('#nav-cart-btn')?.addEventListener('click', () => { refresh(); open(); });
    $('#cart-drawer-close')?.addEventListener('click', close);
    backdrop?.addEventListener('click', close);
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && drawer?.classList.contains('is-open')) close(); });

    /* PDP add-to-bag wiring */
    const addBtn = $('#add-to-bag');
    if (addBtn) {
      addBtn.addEventListener('click', async () => {
        const text = $('#add-to-bag-text');
        const success = $('#add-success');
        const variantInput = $('#pd-variant-id');
        const persName = ($('#pers-name')?.value || '').toUpperCase();
        const persNum = $('#pers-number')?.value || '';

        const properties = {};
        if (persName) properties._personalization_name = persName;
        if (persNum) properties._personalization_number = persNum;

        const formData = {
          id: parseInt(variantInput?.value || '0', 10),
          quantity: 1,
        };
        if (Object.keys(properties).length) formData.properties = properties;

        const result = await add(formData);
        if (result) {
          if (text) text.style.display = 'none';
          if (success) success.style.display = 'inline-block';
          addBtn.style.background = 'var(--success)';
          addBtn.style.borderColor = 'var(--success)';
          setTimeout(() => {
            if (text) text.style.display = '';
            if (success) success.style.display = 'none';
            addBtn.style.background = '';
            addBtn.style.borderColor = '';
          }, 1400);
        }
      });
    }
    $('#sticky-cta-btn')?.addEventListener('click', () => addBtn?.click());

    /* Initial sync (skip in design mode to avoid editor 404 loops) */
    if (!DESIGN_MODE) refresh();

    return { open, close, refresh, add, removeItem };
  })();
  window.GfCart = Cart;

  /* =================================================================
     9. STICKY MOBILE CTA on PDP
     ================================================================= */
  (() => {
    const cta = $('#sticky-cta');
    if (!cta || DESIGN_MODE) return;
    if (window.innerWidth > 768) return;
    const trigger = $('#add-to-bag');
    if (!trigger) { cta.classList.add('is-visible'); return; }
    new IntersectionObserver(entries => entries.forEach(e => cta.classList.toggle('is-visible', !e.isIntersecting)),
      { rootMargin: '0px 0px -80px 0px' }).observe(trigger);
  })();

  /* =================================================================
     10. SCROLL FADE-UP (skip in design mode)
     ================================================================= */
  (() => {
    const targets = $$('.fade-up');
    if (!targets.length) return;
    if (DESIGN_MODE || !('IntersectionObserver' in window)) {
      targets.forEach(node => node.classList.add('is-visible'));
      return;
    }
    const obs = new IntersectionObserver(entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-visible'); obs.unobserve(e.target); } }),
      { rootMargin: '-40px 0px', threshold: 0.04 });
    targets.forEach(node => obs.observe(node));
  })();

  /* =================================================================
     11. WISHLIST (sessionStorage; could move to /cart attributes later)
     ================================================================= */
  (() => {
    let saved;
    try { saved = JSON.parse(sessionStorage.getItem('gf_wishlist_v1') || '[]'); } catch { saved = []; }
    function persist() { try { sessionStorage.setItem('gf_wishlist_v1', JSON.stringify(saved)); } catch {} }
    function syncUI() {
      $$('[data-wishlist]').forEach(btn => {
        btn.classList.toggle('is-active', saved.includes(btn.dataset.wishlist));
      });
    }
    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-wishlist]');
      if (!btn) return;
      e.preventDefault(); e.stopPropagation();
      const id = btn.dataset.wishlist;
      const idx = saved.indexOf(id);
      if (idx >= 0) { saved.splice(idx, 1); announce('Removed from wishlist'); }
      else { saved.push(id); announce('Saved to wishlist'); }
      persist(); syncUI();
    });
    syncUI();
  })();

  /* =================================================================
     12. SHARE BUTTON
     ================================================================= */
  $('#pd-share')?.addEventListener('click', async () => {
    const url = window.location.href;
    const data = { title: document.title, url };
    try {
      if (navigator.share) { await navigator.share(data); announce('Shared'); }
      else { await navigator.clipboard.writeText(url); announce('Link copied to clipboard'); }
    } catch (_) {}
  });

  /* =================================================================
     13. ESTIMATED DELIVERY (zip-aware)
     ================================================================= */
  (() => {
    const target = $('#est-delivery');
    if (target && !target.textContent.trim()) {
      const d = new Date();
      d.setDate(d.getDate() + 9);
      target.textContent = d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    }
    const zip = $('#zip-input');
    zip?.addEventListener('input', () => {
      zip.value = zip.value.replace(/[^0-9]/g, '').slice(0, 5);
      if (zip.value.length === 5 && target) {
        const d = new Date();
        const days = zip.value.startsWith('54') ? 7 : 10;
        d.setDate(d.getDate() + days);
        target.textContent = d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) + ' · ' + zip.value;
      }
    });
  })();

  /* Listen for cart updates from other tabs */
  if (!DESIGN_MODE) {
    window.addEventListener('focus', () => Cart.refresh());
  }

})();
